/**
 * Build-time brand switch for white-label deploys (CRA: set `REACT_APP_BRAND`).
 * Default `divyfi` keeps existing product naming and assets unchanged.
 */

export type BrandId = 'divyfi' | 'pgsplitter';

function normalizeBrandId(raw: string | undefined): BrandId {
  const v = raw?.trim().toLowerCase();
  if (v === 'pgsplitter' || v === 'pg_splitter' || v === 'pg-splitter') return 'pgsplitter';
  return 'divyfi';
}

export const brandId = normalizeBrandId(process.env.REACT_APP_BRAND);

export interface BrandConfig {
  id: BrandId;
  daisyTheme: string;
  storageNamespace: string;
  displayName: string;
  htmlTitle: string;
  metaDescription: string;
  logoHeaderSrc: string;
  logoHeaderAlt: string;
  heroLogoSrc: string;
  heroLogoAlt: string;
  wagmiAppName: string;
  lifiIntegrator: string;
  nav: { secondaryLabel: string; secondaryHref: string };
  footer: {
    copyright: string;
    linkHref: string;
    linkLabel: string;
  };
  dashboard: {
    heroTitle: string;
    heroSubtitle: string;
    docLinkLabel: string;
    connectedSubtitle: string;
    marketingEvmsTitle: string;
    marketingEvmsBody: string;
    marketingGasTitle: string;
    marketingGasBody: string;
    bottomCta: string;
  };
  gasEstimator: { splitterDeployLabel: string };
  /** Shown on whitelabel deploys (e.g. attribution to DivyFi). */
  poweredBy?: { href: string; linkLabel: string };
}

const divyfi: BrandConfig = {
  id: 'divyfi',
  daisyTheme: 'mydark',
  storageNamespace: 'divyfi',
  displayName: 'DivyFi',
  htmlTitle: 'DivyFi',
  metaDescription: 'DivyFi — multi-chain revenue splits, deploy and claim.',
  logoHeaderSrc: '/logotext.png',
  logoHeaderAlt: 'DivyFi',
  heroLogoSrc: '/FullLogo.png',
  heroLogoAlt: 'DivyFi',
  wagmiAppName: 'DivyFi',
  lifiIntegrator: 'DivyFi',
  nav: {
    secondaryLabel: 'Docs',
    secondaryHref: 'https://docs.divyfi.xyz',
  },
  footer: {
    copyright: 'DivyFi 2026',
    linkHref: 'https://proofsec.xyz',
    linkLabel: 'proofsec.xyz',
  },
  dashboard: {
    heroTitle: 'Deploy Multi-Chain Smart Contracts in One Click',
    heroSubtitle:
      'Deploy splitter and other smart contracts across multiple blockchains with automatic gas estimation, cross-chain conversion, and unified execution tracking. Manage balances and activity across all deployments from a single dashboard.',
    docLinkLabel: 'Documentation',
    connectedSubtitle: 'Your EVM splits and activity.',
    marketingEvmsTitle: 'EVM factories',
    marketingEvmsBody:
      'Splits indexed by the factory: see contracts where you are a payee, with native and token claim history.',
    marketingGasTitle: 'Gas & funding',
    marketingGasBody: 'Estimate native gas and USD for factory deploys before you confirm in wallet.',
    bottomCta: 'Read the full product overview and security notes in the docs.',
  },
  gasEstimator: {
    splitterDeployLabel: 'DivyFi splitter contract deploy: ',
  },
};

const pgsplitter: BrandConfig = {
  id: 'pgsplitter',
  daisyTheme: 'pgsplitter',
  storageNamespace: 'pgsplitter',
  displayName: 'pgSplitter',
  htmlTitle: 'pgSplitter',
  metaDescription:
    'pgSplitter — PulseChain & EVM revenue splits for the Just A Pulse Guy ecosystem. Deploy, claim, and coordinate payees.',
  logoHeaderSrc: '/pgsplitter/pulseguy.jpg',
  logoHeaderAlt: '$PulseGuy · pgSplitter',
  heroLogoSrc: '/pgsplitter/pulseguy.jpg',
  heroLogoAlt: '$PulseGuy · pgSplitter',
  wagmiAppName: 'pgSplitter',
  lifiIntegrator: 'pgSplitter',
  nav: {
    secondaryLabel: '$PulseGuy',
    secondaryHref: 'https://pulseguy.linkme.one',
  },
  footer: {
    copyright: 'pgSplitter 2026 · Just A Pulse Guy ecosystem',
    linkHref: 'https://pulseguy.linkme.one',
    linkLabel: '$PulseGuy',
  },
  dashboard: {
    heroTitle: 'Revenue splits on PulseChain & EVM',
    heroSubtitle:
      'Deploy audited payment splitters from shared factories—tuned for the Just A Pulse Guy / $PULSEGUY community on PulseChain. Route, trade, and link out via $PulseGuy (pulseguy.linkme.one), then split proceeds across payees in one place.',
    docLinkLabel: '$PulseGuy',
    connectedSubtitle: 'Your splits on PulseChain and other supported EVM networks.',
    marketingEvmsTitle: 'EVM factories',
    marketingEvmsBody:
      'PulseChain and multi-chain factories: find splitters where you are a payee, track native and ERC-20 balances, and claim your share.',
    marketingGasTitle: 'Gas & funding',
    marketingGasBody:
      'Estimate native gas and USD before you confirm in wallet—plan PulseChain deploys alongside your other targets.',
    bottomCta: 'Visit $PulseGuy on pulseguy.linkme.one for ecosystem links and the wider Pulse Guy toolkit.',
  },
  gasEstimator: {
    splitterDeployLabel: 'Splitter contract deploy (gas units): ',
  },
  poweredBy: {
    href: 'https://divyfi.xyz',
    linkLabel: 'https://divyfi.xyz',
  },
};

const byId: Record<BrandId, BrandConfig> = {
  divyfi,
  pgsplitter,
};

export const brand = byId[brandId];

export function assetUrl(path: string): string {
  const base = process.env.PUBLIC_URL ?? '';
  return `${base}${path}`;
}

export const storageKeys = {
  deployedSplitsBase: `${brand.storageNamespace}:deployed-splits`,
  gasEstimatorChains: `${brand.storageNamespace}.gasEstimator.selectedChains`,
  erc20Splitter: (chainId: number, splitterLower: string) =>
    `${brand.storageNamespace}:erc20:${chainId}:${splitterLower}`,
  splitterLabel: (chainId: number, splitterLower: string) =>
    `${brand.storageNamespace}:splitter:label:${chainId}:${splitterLower}`,
};
